type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Translate a Zod / backend validation message into the user's language when a
 * recognizable pattern is present. Falls back to the original English message
 * so unknown errors remain readable rather than blank.
 *
 * Coverage (each returns its i18n key if matched):
 *   - Zod string.min/max                    -> validation.minLength / .maxLength
 *   - Zod number.min/max (gte / lte / lt)   -> validation.minValue / .maxValue
 *   - Zod .positive() / .nonnegative()      -> validation.mustBePositive
 *   - Zod enum / invalid option             -> validation.invalidOption
 *   - Zod .url() / .uuid() / .email()       -> validation.invalidUrl / .invalidUuid / .invalidEmail
 *   - Zod required / missing                -> validation.required
 *   - Zod type mismatch ('Expected X, ..')  -> validation.invalidType
 *   - Backend business rules emitted by     -> dedicated validation.* keys
 *     our own handlers (name already taken,
 *     load group different site, etc.)
 *
 * Anything else passes through verbatim so unfamiliar errors stay readable.
 */
export function translateServerMessage(msg: string, translate: Translate): string {
  if (!msg) return msg;

  // ---- Project-specific business-rule strings (emitted from our handlers) ----
  // Matched as loose substrings so slight backend wording variations still hit.
  if (/name already exists in this site/i.test(msg)) {
    return translate('validation.nameAlreadyExistsInSite', { _: msg });
  }
  if (/priority level already exists in this site/i.test(msg)) {
    return translate('validation.priorityAlreadyExistsInSite', { _: msg });
  }
  // Fee-template variant must win over the generic /template name/ pattern
  // below — otherwise OCPP-template regex swallows it.
  if (/fee template name already exists/i.test(msg)) {
    return translate('validation.feeTemplateNameAlreadyExists', { _: msg });
  }
  if (/template name already exists/i.test(msg)) {
    return translate('validation.templateNameAlreadyExists', { _: msg });
  }
  if (/manual code already in use/i.test(msg)) {
    return translate('validation.manualCodeAlreadyInUse', { _: msg });
  }
  if (/manual code must be 4 characters/i.test(msg)) {
    return translate('validation.manualCodeFormat', { _: msg });
  }
  if (/single phase voltage cannot exceed 480v/i.test(msg)) {
    return translate('validation.singlePhaseVoltageCap', { _: msg });
  }
  const maxPowerBelowMatch = msg.match(/cannot reduce max power below ([\d.]+)kw/i);
  if (maxPowerBelowMatch) {
    return translate('validation.maxPowerBelowCapacity', { min: maxPowerBelowMatch[1], _: msg });
  }
  if (/site not found/i.test(msg)) {
    return translate('validation.siteNotFound', { _: msg });
  }
  if (/load group belongs to a different site/i.test(msg)) {
    return translate('validation.loadGroupDifferentSite', { _: msg });
  }
  if (/load group not found/i.test(msg)) {
    return translate('validation.loadGroupNotFound', { _: msg });
  }
  if (/charger type id already exists/i.test(msg)) {
    return translate('validation.chargerTypeIdAlreadyExists', { _: msg });
  }
  if (/charger type name already exists/i.test(msg)) {
    return translate('validation.chargerTypeNameAlreadyExists', { _: msg });
  }
  if (/firmware version already exists/i.test(msg)) {
    return translate('validation.firmwareVersionAlreadyExists', { _: msg });
  }
  // More-specific 'for bulk updates' variants must come before the generic
  // vendor/model required patterns, otherwise the shorter regex swallows them.
  if (/vendor is required for bulk updates/i.test(msg)) {
    return translate('validation.bulkVendorRequired', { _: msg });
  }
  if (/model is required for bulk updates/i.test(msg)) {
    return translate('validation.bulkModelRequired', { _: msg });
  }
  if (/file is required/i.test(msg)) {
    return translate('validation.fileRequired', { _: msg });
  }
  if (/vendor is required/i.test(msg)) {
    return translate('validation.vendorRequired', { _: msg });
  }
  if (/model is required/i.test(msg)) {
    return translate('validation.modelRequired', { _: msg });
  }
  if (/version is required/i.test(msg)) {
    return translate('validation.versionRequired', { _: msg });
  }
  if (/(vendor|model|version) must not be empty/i.test(msg)) {
    return translate('validation.mustNotBeEmpty', { _: msg });
  }
  if (/vendor code already exists/i.test(msg)) {
    return translate('validation.vendorCodeAlreadyExists', { _: msg });
  }
  if (/target_type must be one of/i.test(msg)) {
    return translate('validation.targetTypeInvalid', { _: msg });
  }
  if (/^firmware not found$/i.test(msg)) {
    return translate('validation.firmwareNotFound', { _: msg });
  }
  if (/cannot use inactive firmware for updates/i.test(msg)) {
    return translate('validation.firmwareInactive', { _: msg });
  }
  if (/^vendor not found$/i.test(msg)) {
    return translate('validation.vendorNotFound', { _: msg });
  }
  if (/phone number already exists/i.test(msg)) {
    return translate('validation.phoneNumberAlreadyExists', { _: msg });
  }
  if (/^app user not found$/i.test(msg)) {
    return translate('validation.appUserNotFound', { _: msg });
  }
  if (/^rfid user not found$/i.test(msg)) {
    return translate('validation.rfidUserNotFound', { _: msg });
  }
  if (/must be linked to either an app user or an rfid user, but not both/i.test(msg)) {
    return translate('validation.cardLinkExclusive', { _: msg });
  }

  // -- Users / roles / supabase-connections --
  if (/username is already in use/i.test(msg)) {
    return translate('validation.usernameInUse', { _: msg });
  }
  if (/email( address)? is already in use/i.test(msg)) {
    return translate('validation.emailInUse', { _: msg });
  }
  if (/role name already exists/i.test(msg)) {
    return translate('validation.roleNameAlreadyExists', { _: msg });
  }
  if (/supabase connection with this name already exists/i.test(msg)) {
    return translate('validation.supabaseConnectionNameAlreadyExists', { _: msg });
  }

  // -- Fee templates / fee schedules / promotions --
  if (/fee template not found/i.test(msg)) {
    return translate('validation.feeTemplateNotFound', { _: msg });
  }
  if (/cannot use inactive template/i.test(msg)) {
    return translate('validation.feeTemplateInactive', { _: msg });
  }
  const entityNotFoundMatch = msg.match(/^(\w+) with ID \S+ not found$/i);
  if (entityNotFoundMatch) {
    return translate('validation.entityNotFound', { type: entityNotFoundMatch[1], _: msg });
  }
  if (/(schedule overlaps with an existing schedule|update would create overlapping schedule)/i.test(msg)) {
    return translate('validation.scheduleOverlap', { _: msg });
  }
  if (/fee structure is required when not using a template/i.test(msg)) {
    return translate('validation.feeStructureRequired', { _: msg });
  }
  if (/currency is required when not using a template/i.test(msg)) {
    return translate('validation.currencyRequired', { _: msg });
  }
  if (/promotion with this code already exists/i.test(msg)) {
    return translate('validation.promotionCodeAlreadyExists', { _: msg });
  }
  if (/shared code type requires a code/i.test(msg)) {
    return translate('validation.sharedCodeRequired', { _: msg });
  }
  if (/code type "?none"? should not have a code/i.test(msg)) {
    return translate('validation.codeTypeNoneNoCode', { _: msg });
  }
  if (/valid until must be after valid from/i.test(msg)) {
    return translate('validation.validUntilAfterValidFrom', { _: msg });
  }

  // -- Banners --
  if (/image url is required for image banners/i.test(msg)) {
    return translate('validation.imageUrlRequired', { _: msg });
  }
  if (/gradient start color is required/i.test(msg)) {
    return translate('validation.gradientStartColorRequired', { _: msg });
  }
  if (/gradient end color is required/i.test(msg)) {
    return translate('validation.gradientEndColorRequired', { _: msg });
  }
  if (/display type must be image or gradient/i.test(msg)) {
    return translate('validation.displayTypeInvalid', { _: msg });
  }
  if (/^title (is required|cannot be empty)$/i.test(msg)) {
    return translate('validation.titleRequired', { _: msg });
  }

  // -- Notification groups / settings / profile --
  if (/group with this name already exists/i.test(msg)) {
    return translate('validation.groupNameAlreadyExists', { _: msg });
  }
  if (/provided timezone is not valid|invalid timezone/i.test(msg)) {
    return translate('validation.timezoneInvalid', { _: msg });
  }
  if (/setting with this key already exists/i.test(msg)) {
    return translate('validation.settingKeyAlreadyExists', { _: msg });
  }
  if (/current password is incorrect/i.test(msg)) {
    return translate('validation.currentPasswordIncorrect', { _: msg });
  }
  if (/verification code is invalid or expired/i.test(msg)) {
    return translate('validation.verificationCodeInvalid', { _: msg });
  }
  if (/^password is incorrect$/i.test(msg)) {
    return translate('validation.passwordIncorrect', { _: msg });
  }
  if (/^password is required$/i.test(msg)) {
    return translate('validation.passwordRequired', { _: msg });
  }

  // ---- Generic Zod patterns ----
  // Character-set rule used by the LMS template name regex (now removed; kept
  // defensively in case any other form re-introduces this restriction).
  if (/letters, numbers, spaces, hyphens, and underscores/i.test(msg)) {
    return translate('validation.nameInvalidChars', { _: msg });
  }

  const maxLenCharMatch = msg.match(/(?:must contain at most|less than) (\d+) character/i);
  if (maxLenCharMatch) {
    return translate('validation.maxLength', { count: Number(maxLenCharMatch[1]), _: msg });
  }

  const minLenCharMatch = msg.match(/must contain at least (\d+) character/i);
  if (minLenCharMatch) {
    return translate('validation.minLength', { count: Number(minLenCharMatch[1]), _: msg });
  }

  // Zod number.min / number.max messages typically read like
  // "Number must be greater than or equal to 3" etc.
  const minNumMatch = msg.match(/(?:greater than or equal to|at least) ([\d.]+)/i);
  if (minNumMatch && !/character/i.test(msg)) {
    return translate('validation.minValue', { min: minNumMatch[1], _: msg });
  }

  const maxNumMatch = msg.match(/(?:less than or equal to|at most) ([\d.]+)/i);
  if (maxNumMatch && !/character/i.test(msg)) {
    return translate('validation.maxValue', { max: maxNumMatch[1], _: msg });
  }

  // .positive() / .nonnegative() — "Number must be greater than 0"
  if (/must be greater than (?:or equal to )?0\b/i.test(msg) || /must be positive/i.test(msg)) {
    return translate('validation.mustBePositive', { _: msg });
  }

  // Format validators
  if (/^invalid url$/i.test(msg) || /invalid url/i.test(msg)) {
    return translate('validation.invalidUrl', { _: msg });
  }
  if (/^invalid uuid$/i.test(msg) || /invalid uuid/i.test(msg)) {
    return translate('validation.invalidUuid', { _: msg });
  }
  if (/^invalid email$/i.test(msg) || /invalid email/i.test(msg)) {
    return translate('validation.invalidEmail', { _: msg });
  }

  // Type mismatch — "Expected number, received string" etc.
  if (/^expected \w+, received /i.test(msg)) {
    return translate('validation.invalidType', { _: msg });
  }

  if (/^name is required$/i.test(msg) || /^required$/i.test(msg) || /is required$/i.test(msg)) {
    return translate('validation.required', { _: msg });
  }

  if (/invalid enum value/i.test(msg) || /invalid option/i.test(msg)) {
    return translate('validation.invalidOption', { _: msg });
  }

  return msg;
}
